import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authApi } from '../services/api';

function parsePermissions(permissions: string): string[] {
  if (!permissions || permissions === 'all') return ['all'];
  try {
    const parsed = JSON.parse(permissions);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  permissions: string;
  avatar?: string;
  phone?: string;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  mustChangePassword: boolean;
  isAdmin: () => boolean;
  hasModule: (module: string) => boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      mustChangePassword: false,

      isAdmin: () => get().user?.role === 'ADMIN',

      hasModule: (module: string) => {
        const user = get().user;
        if (!user) return false;
        if (user.role === 'ADMIN') return true;
        const perms = parsePermissions(user.permissions);
        return perms.includes('all') || perms.includes(module);
      },

      login: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
          const { data } = await authApi.login(email, password);
          set({
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            isAuthenticated: true,
            mustChangePassword: data.mustChangePassword,
          });

          // Fetch full profile
          const profile = await authApi.me();
          set({ user: profile.data, isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: () => {
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          mustChangePassword: false,
        });
      },

      checkAuth: async () => {
        const { accessToken } = get();
        if (!accessToken) return;

        try {
          const { data } = await authApi.me();
          set({ user: data, isAuthenticated: true });
        } catch {
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
          });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        mustChangePassword: state.mustChangePassword,
      }),
    },
  ),
);
